import { faList } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Bolt from "@mui/icons-material/Bolt";
import FolderCopy from "@mui/icons-material/FolderCopy";
import DataObject from "@mui/icons-material/DataObject";
import Functions from "@mui/icons-material/Functions";
import Laptop from "@mui/icons-material/Laptop";
import Person from "@mui/icons-material/Person";
import TableChart from "@mui/icons-material/TableChart";
import TipsAndUpdates from "@mui/icons-material/TipsAndUpdates";
import { Box } from "@mui/material";
import type { ReactNode } from "react";

import { ALL_GROUPS } from "./constants";
import type { ResourceCategoryV2, VisibleGroupKey } from "./types";

export function tableFieldCategory(groupKey: VisibleGroupKey): ResourceCategoryV2 {
	if (groupKey === "screenTables") return "screenTableField";
	if (groupKey === "singleRowDatabaseVariables") return "singleRowDbVariableField";
	if (groupKey === "multiRowDatabaseVariables") return "multiRowDbVariableField";
	return "databaseTableField";
}

export function groupLabel(key: VisibleGroupKey): string {
	const labels: Record<VisibleGroupKey, string> = {
		screenFields: "Screen Fields",
		advancedTriggers: "Advanced Triggers",
		collections: "Collections",
		currentCollection: "Current Collection",
		otherCollections: "Other Collections",
		suggestedFields: "Suggested Fields",
		simpleVariable: "Simple Variable",
		formulaVariable: "Formula Variable",
		singleRowDatabaseVariables: "Database Table Variable - Single Row",
		multiRowDatabaseVariables: "Database Table Variable - Multi Row",
		screenTables: "Screen Tables",
		otherScreenLocations: "Other Screens with Fields",
		globalConstants: "Global Constants",
		reactions: "Reactions",
	};
	return labels[key];
}

export function groupKeyFromGroupLabel(label: string): VisibleGroupKey | undefined {
	for (const key of ALL_GROUPS) {
		if (groupLabel(key) === label) return key;
	}
	return undefined;
}

// Map each picker section to one of the 6 brand gradients. The icon glyph is
// painted with the gradient via the global `<linearGradient>` defs (see
// CoAppGradientDefs); the surrounding tile gets a low-alpha tint of the same
// gradient so the section header reads as a coherent group without competing
// with the option labels below.
function groupGradId(key: VisibleGroupKey): string {
	const ids: Record<VisibleGroupKey, string> = {
		screenFields: "primary",
		advancedTriggers: "primary",
		collections: "blue",
		currentCollection: "blue",
		otherCollections: "blue",
		suggestedFields: "primary",
		simpleVariable: "yellow",
		formulaVariable: "purple",
		singleRowDatabaseVariables: "orange",
		multiRowDatabaseVariables: "orange",
		screenTables: "blue",
		otherScreenLocations: "blue",
		globalConstants: "primary",
		reactions: "primary",
	};
	return ids[key];
}

/** Matches nav sidebar `iconKind: "collections"` (faList) with collections blue gradient. */
export function collectionNavIcon(): ReactNode {
	const gradId = "blue";
	return (
		<Box
			component="span"
			sx={{
				display: "inline-flex",
				alignItems: "center",
				justifyContent: "center",
				width: 22,
				height: 22,
				borderRadius: 1.25,
				background: `var(--coapp-grad-${gradId}-tint, rgba(2, 213, 171, 0.10))`,
				flexShrink: 0,
				lineHeight: 0,
				"& svg": {
					width: 12,
					height: 12,
					display: "block",
				},
				"& svg path": {
					fill: `url(#coapp-grad-${gradId})`,
				},
			}}
		>
			<FontAwesomeIcon icon={faList} />
		</Box>
	);
}

export function groupIcon(key: VisibleGroupKey): ReactNode {
	const gradId = groupGradId(key);
	const Icon = (() => {
		switch (key) {
			case "screenFields":
				return Laptop;
			case "advancedTriggers":
				return Bolt;
			case "collections":
			case "currentCollection":
			case "otherCollections":
				return FolderCopy;
			case "suggestedFields": return TipsAndUpdates;
			case "simpleVariable": return DataObject;
			case "formulaVariable": return Functions;
			case "singleRowDatabaseVariables":
			case "multiRowDatabaseVariables": return TableChart;
			case "screenTables":
			case "otherScreenLocations": return Laptop;
			case "globalConstants": return Person;
			case "reactions": return TipsAndUpdates;
			default: return DataObject;
		}
	})();
	return (
		<Box
			component="span"
			sx={{
				display: "inline-flex",
				alignItems: "center",
				justifyContent: "center",
				width: 22,
				height: 22,
				borderRadius: 1.25,
				background: `var(--coapp-grad-${gradId}-tint, rgba(2, 213, 171, 0.10))`,
				flexShrink: 0,
			}}
		>
			<Icon
				fontSize="small"
				sx={{
					fontSize: 14,
					"& path, & rect": { fill: `url(#coapp-grad-${gradId})` }
				}}
			/>
		</Box>
	);
}
